import React from "react";

import not from "ramda/src/not";
import prop from "ramda/src/prop";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";

import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { useNetwork } from "../../hooks/useNetwork";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Switch } from "../Switch";

const getLabel = (isActive: boolean) => (isActive ? "Active" : "Not Active");

export const PostMatchingVisibilityToggle: React.FC = () => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const { updateSubNetworkValues } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getL2s } = useNetwork();

  const l2s = getL2s(selectedNetworkId);
  const firstL2 = l2s[0];
  const [value, setValue] = React.useState(firstL2?.postMatchingMatchesVisible ?? false);

  React.useEffect(() => {
    setValue(firstL2?.postMatchingMatchesVisible ?? false);
  }, [firstL2?.postMatchingMatchesVisible]);

  const updateLocalNetworkData = React.useCallback(
    (postMatchingMatchesVisible: boolean) => () => {
      l2s.forEach((l2) => {
        updateSubNetworkValues(selectedNetworkId, l2.l2Id, {
          postMatchingMatchesVisible,
        });
      });
    },
    [selectedNetworkId, l2s, updateSubNetworkValues]
  );

  const stopLoading = () => setIsLoading(false);

  const handleError = () =>
    showErrorSnackbar("An error occurred. Please try again");

  const onComplete = () => showSuccessSnackbar("Saved Changes");

  const handleSuccess = (postMatchingMatchesVisible: boolean) =>
    ifElse(
      prop("success"),
      compose(onComplete, updateLocalNetworkData(postMatchingMatchesVisible)),
      handleError
    );

  const handleChange = async (
    _: React.ChangeEvent<HTMLInputElement>,
    postMatchingMatchesVisible: boolean
  ) => {
    const body = {
      subnetworkCommands: l2s.map((l2) => ({
        l1Id: selectedNetworkId,
        l2Id: l2.l2Id,
      })),
      unitTypeCommands: [],
      postMatchingMatchesVisible,
    };

    setIsLoading(true);
    setValue(not);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(handleSuccess(postMatchingMatchesVisible))
      .catch(handleError)
      .finally(stopLoading);
  };

  return (
    <ReadOnlyWrapper>
      <Switch
        isDisabled={isLoading || isSuperAdminRead()}
        label={getLabel(value)}
        switchProps={{ checked: value, onChange: handleChange }}
      />
    </ReadOnlyWrapper>
  );
};
